import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, X, ChevronRight, Package } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/utils'
import { useAuth } from '../context/AuthContext'
import CsvControls from '../components/CsvControls'

interface Addon {
  id: string
  name_en: string
  name_ar: string
  price: number
  active: number
  sort_order: number
  product_count?: number
}

interface Product {
  id: string
  name: string
  category_id: string | null
  category_name: string | null
}

interface Category {
  id: string
  name: string
  parent_id: string | null
}

function AddonModal({ addon, onClose }: { addon: Addon | 'new'; onClose: () => void }) {
  const isNew = addon === 'new'
  const qc = useQueryClient()

  const [nameEn, setNameEn] = useState(isNew ? '' : (addon as Addon).name_en)
  const [nameAr, setNameAr] = useState(isNew ? '' : (addon as Addon).name_ar)
  const [price, setPrice] = useState(isNew ? '0' : String((addon as Addon).price))
  const [sortOrder, setSortOrder] = useState(isNew ? '0' : String((addon as Addon).sort_order))
  const [error, setError] = useState('')

  const save = useMutation({
    mutationFn: (payload: object) =>
      isNew
        ? api.post('/addons', payload).then(r => r.data)
        : api.patch(`/addons/${(addon as Addon).id}`, payload).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['addons'] }); onClose() },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to save'),
  })

  const handleSubmit = () => {
    if (!nameEn.trim() || !nameAr.trim()) { setError('Both names are required'); return }
    const priceVal = parseInt(price, 10)
    if (isNaN(priceVal) || priceVal < 0) { setError('Price must be 0 or more (fils)'); return }
    save.mutate({
      name_en: nameEn.trim(),
      name_ar: nameAr.trim(),
      price: priceVal,
      sort_order: parseInt(sortOrder, 10) || 0,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 w-full max-w-md p-6"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-semibold text-white">{isNew ? 'New Add-on' : 'Edit Add-on'}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-gray-400"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Name (English)</label>
            <input value={nameEn} onChange={e => setNameEn(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              placeholder="e.g. Branded Cup" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Name (Arabic)</label>
            <input value={nameAr} onChange={e => setNameAr(e.target.value)} dir="rtl"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              placeholder="مثال: كوب DrinX" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-400 mb-1">Price (fils) — 0 = free</label>
              <input type="number" min="0" value={price} onChange={e => setPrice(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a]" />
              {parseInt(price, 10) > 0 && (
                <p className="text-xs text-gray-500 mt-1">{formatCurrency(parseInt(price, 10))}</p>
              )}
            </div>
            <div className="w-28">
              <label className="block text-xs text-gray-400 mb-1">Sort order</label>
              <input type="number" min="0" value={sortOrder} onChange={e => setSortOrder(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a]" />
            </div>
          </div>
        </div>

        {error && <p className="text-red-400 text-xs mt-3">{error}</p>}

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">Cancel</button>
          <button onClick={handleSubmit} disabled={save.isPending}
            className="px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Checkbox({ checked }: { checked: boolean | 'indeterminate' }) {
  return (
    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
      checked ? 'bg-[#c41e3a] border-[#c41e3a]' : 'border-white/20'
    }`}>
      {checked === true && (
        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
      {checked === 'indeterminate' && (
        <div className="w-2.5 h-0.5 bg-white rounded-full" />
      )}
    </div>
  )
}

function Pill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
        active ? 'bg-[#c41e3a] text-white' : 'bg-white/10 text-gray-400 hover:text-white hover:bg-white/15'
      }`}>
      {label}
    </button>
  )
}

function ProductAssignPanel({ addon, onClose }: { addon: Addon; onClose: () => void }) {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null)
  const [selectedSubId, setSelectedSubId] = useState<string | null>(null)
  const [draftIds, setDraftIds] = useState<Set<string> | null>(null)

  const { data: productsData } = useQuery<{ products: Product[] }>({
    queryKey: ['products-all'],
    queryFn: () => api.get('/products?limit=1000').then(r => r.data),
  })

  const { data: categoriesData } = useQuery<{ categories: Category[] }>({
    queryKey: ['categories'],
    queryFn: () => api.get('/categories').then(r => r.data),
  })

  const { data: assignedData } = useQuery<{ product_ids: string[] }>({
    queryKey: ['addon-products', addon.id],
    queryFn: () => api.get(`/addons/${addon.id}/products`).then(r => r.data),
  })

  useEffect(() => {
    if (assignedData && draftIds === null) {
      setDraftIds(new Set(assignedData.product_ids))
    }
  }, [assignedData])

  const save = useMutation({
    mutationFn: (ids: string[]) =>
      api.put(`/addons/${addon.id}/products`, { product_ids: ids }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['addon-products', addon.id] })
      qc.invalidateQueries({ queryKey: ['addons'] })
    },
  })

  const allProducts = productsData?.products ?? []
  const categories = categoriesData?.categories ?? []
  const serverIds = new Set(assignedData?.product_ids ?? [])
  const ids = draftIds ?? serverIds

  const isDirty = draftIds !== null && (
    draftIds.size !== serverIds.size || [...draftIds].some(id => !serverIds.has(id))
  )
  const addedCount = draftIds ? [...draftIds].filter(id => !serverIds.has(id)).length : 0
  const removedCount = draftIds ? [...serverIds].filter(id => !draftIds.has(id)).length : 0
  const changeCount = addedCount + removedCount

  // Derived category lists
  const parentCats = categories
    .filter(c => c.parent_id === null)
    .filter(parent => {
      const children = categories.filter(c => c.parent_id === parent.id)
      return children.length > 0
        ? allProducts.some(p => children.some(c => c.id === p.category_id))
        : allProducts.some(p => p.category_id === parent.id)
    })

  const subCats = selectedParentId
    ? categories
        .filter(c => c.parent_id === selectedParentId)
        .filter(sub => allProducts.some(p => p.category_id === sub.id))
    : []

  const selectParent = (id: string | null) => {
    setSelectedParentId(id)
    setSelectedSubId(null)
    setSearch('')
  }

  const selectSub = (id: string | null) => setSelectedSubId(id)

  // Determine visible product list (null = hierarchical tree)
  const filteredProducts: Product[] | null = (() => {
    if (search.trim()) {
      return allProducts.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    }
    if (selectedSubId) {
      return allProducts.filter(p => p.category_id === selectedSubId)
    }
    if (selectedParentId) {
      const children = categories.filter(c => c.parent_id === selectedParentId)
      const childIds = children.map(c => c.id)
      return children.length > 0
        ? allProducts.filter(p => childIds.includes(p.category_id ?? ''))
        : allProducts.filter(p => p.category_id === selectedParentId)
    }
    return null
  })()

  // Toggle helpers — all update draftIds only, never fire API
  const toggle = (productId: string) => {
    setDraftIds(prev => {
      const next = new Set(prev ?? serverIds)
      if (next.has(productId)) next.delete(productId)
      else next.add(productId)
      return next
    })
  }

  const toggleGroup = (catIds: string[]) => {
    const prods = allProducts.filter(p => p.category_id && catIds.includes(p.category_id))
    const allIn = prods.every(p => ids.has(p.id))
    setDraftIds(prev => {
      const next = new Set(prev ?? serverIds)
      if (allIn) prods.forEach(p => next.delete(p.id))
      else prods.forEach(p => next.add(p.id))
      return next
    })
  }

  const toggleAll = () => {
    const target = filteredProducts ?? allProducts
    const allIn = target.every(p => ids.has(p.id))
    setDraftIds(prev => {
      const next = new Set(prev ?? serverIds)
      if (allIn) target.forEach(p => next.delete(p.id))
      else target.forEach(p => next.add(p.id))
      return next
    })
  }

  const groupState = (catIds: string[]): boolean | 'indeterminate' => {
    const prods = allProducts.filter(p => p.category_id && catIds.includes(p.category_id))
    if (!prods.length) return false
    const n = prods.filter(p => ids.has(p.id)).length
    return n === 0 ? false : n === prods.length ? true : 'indeterminate'
  }

  const handleSave = () => save.mutate([...ids])
  const handleReset = () => setDraftIds(new Set(serverIds))

  const visibleCount = (filteredProducts ?? allProducts).length
  const allVisibleIn = visibleCount > 0 && (filteredProducts ?? allProducts).every(p => ids.has(p.id))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/40"
         onClick={() => { if (!isDirty) onClose() }}>
      <div className="h-full w-full max-w-sm bg-[#1a1a1a] border-l border-white/10 flex flex-col"
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
          <div>
            <p className="text-xs text-gray-400">Assign to products</p>
            <p className="font-semibold text-white">{addon.name_en}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {ids.size > 0
                ? <span className="text-[#c41e3a]">{ids.size} product{ids.size !== 1 ? 's' : ''} selected</span>
                : 'No products selected'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        {/* Search */}
        <div className="px-4 pt-3 pb-2 shrink-0">
          <input value={search}
            onChange={e => { setSearch(e.target.value); setSelectedParentId(null); setSelectedSubId(null) }}
            placeholder="Search products…"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]" />
        </div>

        {/* Category pills */}
        {!search.trim() && (
          <div className="shrink-0 px-4 pb-2 space-y-1.5">
            <div className="flex gap-1.5 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
              <Pill label="All" active={selectedParentId === null} onClick={() => selectParent(null)} />
              {parentCats.map(cat => (
                <Pill key={cat.id} label={cat.name}
                  active={selectedParentId === cat.id}
                  onClick={() => selectParent(selectedParentId === cat.id ? null : cat.id)} />
              ))}
            </div>
            {subCats.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
                <Pill label="All" active={selectedSubId === null}
                  onClick={() => selectSub(null)} />
                {subCats.map(sub => (
                  <Pill key={sub.id} label={sub.name}
                    active={selectedSubId === sub.id}
                    onClick={() => selectSub(selectedSubId === sub.id ? null : sub.id)} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-1.5 border-y border-white/5 shrink-0">
          <span className="text-xs text-gray-600">{visibleCount} product{visibleCount !== 1 ? 's' : ''}</span>
          {visibleCount > 0 && (
            <button onClick={toggleAll} className="text-xs text-gray-400 hover:text-white transition-colors">
              {allVisibleIn ? 'Deselect all' : 'Select all'}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {filteredProducts !== null ? (
            filteredProducts.length === 0 ? (
              <p className="text-center text-gray-500 text-sm py-10">No products found</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {filteredProducts.map(p => (
                  <li key={p.id}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 cursor-pointer"
                      onClick={() => toggle(p.id)}>
                    <Checkbox checked={ids.has(p.id)} />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{p.name}</p>
                      {p.category_name && <p className="text-xs text-gray-500 truncate">{p.category_name}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )
          ) : (
            /* Hierarchical tree */
            categories.length === 0 ? (
              <p className="text-center text-gray-500 text-sm py-10">Loading…</p>
            ) : (
              parentCats.map(parent => {
                const children = categories.filter(c => c.parent_id === parent.id)
                const directProducts = allProducts.filter(p => p.category_id === parent.id)
                const childIds = children.map(c => c.id)
                const parentState = children.length > 0 ? groupState(childIds) : groupState([parent.id])
                const hasContent = directProducts.length > 0 || children.some(c =>
                  allProducts.some(p => p.category_id === c.id)
                )
                if (!hasContent) return null
                return (
                  <div key={parent.id}>
                    <div className="flex items-center gap-3 px-4 py-2.5 bg-white/[0.06] border-b border-white/10 cursor-pointer hover:bg-white/[0.09] sticky top-0"
                         onClick={() => toggleGroup(children.length > 0 ? childIds : [parent.id])}>
                      <Checkbox checked={parentState} />
                      <span className="text-xs font-bold text-white uppercase tracking-widest">{parent.name}</span>
                    </div>
                    {children.length > 0 ? children.map(sub => {
                      const subProducts = allProducts.filter(p => p.category_id === sub.id)
                      if (!subProducts.length) return null
                      return (
                        <div key={sub.id}>
                          <div className="flex items-center gap-3 pl-8 pr-4 py-2 bg-white/[0.02] border-b border-white/5 cursor-pointer hover:bg-white/[0.05]"
                               onClick={() => toggleGroup([sub.id])}>
                            <Checkbox checked={groupState([sub.id])} />
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{sub.name}</span>
                            <span className="ml-auto text-xs text-gray-600">{subProducts.length}</span>
                          </div>
                          <ul className="divide-y divide-white/5">
                            {subProducts.map(p => (
                              <li key={p.id}
                                  className="flex items-center gap-3 pl-14 pr-4 py-2.5 hover:bg-white/5 cursor-pointer"
                                  onClick={() => toggle(p.id)}>
                                <Checkbox checked={ids.has(p.id)} />
                                <span className="text-sm text-white truncate">{p.name}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    }) : (
                      <ul className="divide-y divide-white/5">
                        {directProducts.map(p => (
                          <li key={p.id}
                              className="flex items-center gap-3 pl-10 pr-4 py-2.5 hover:bg-white/5 cursor-pointer"
                              onClick={() => toggle(p.id)}>
                            <Checkbox checked={ids.has(p.id)} />
                            <span className="text-sm text-white truncate">{p.name}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )
              })
            )
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-4 py-3 border-t border-white/10 flex items-center gap-3">
          {isDirty ? (
            <span className="text-xs text-amber-400 flex-1">
              {addedCount > 0 && `+${addedCount}`}{addedCount > 0 && removedCount > 0 && ' / '}{removedCount > 0 && `-${removedCount}`} unsaved
            </span>
          ) : (
            <span className="text-xs text-gray-600 flex-1">{save.isSuccess ? 'Saved' : 'No changes'}</span>
          )}
          <button onClick={handleReset} disabled={!isDirty}
            className="px-3 py-1.5 rounded-lg text-sm text-gray-400 hover:text-white disabled:opacity-30 transition-colors">
            Reset
          </button>
          <button onClick={handleSave} disabled={!isDirty || save.isPending}
            className="px-4 py-1.5 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-40 transition-colors">
            {save.isPending ? 'Saving…' : `Save${changeCount > 0 ? ` (${changeCount})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Addons() {
  const { can } = useAuth()
  const qc = useQueryClient()
  const [modal, setModal] = useState<Addon | 'new' | null>(null)
  const [assignPanel, setAssignPanel] = useState<Addon | null>(null)

  const { data, isLoading } = useQuery<{ addons: Addon[] }>({
    queryKey: ['addons'],
    queryFn: () => api.get('/addons').then(r => r.data),
  })

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/addons/${id}`, { active }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addons'] }),
  })

  const deleteAddon = useMutation({
    mutationFn: (id: string) => api.delete(`/addons/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addons'] }),
  })

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Add-ons</h1>
        {can(['super_admin', 'operations']) && (
          <div className="flex items-center gap-2">
            <CsvControls
              basePath="/addons"
              templateFilename="addons-template.csv"
              exportFilename={`addons-export-${new Date().toISOString().slice(0, 10)}.csv`}
              label="add-on"
              onImported={() => qc.invalidateQueries({ queryKey: ['addons'] })}
            />
            <button onClick={() => setModal('new')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]">
              <Plus size={16} /> New Add-on
            </button>
          </div>
        )}
      </div>

      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <p className="text-center text-gray-500 text-sm py-10">Loading…</p>
        ) : !data?.addons.length ? (
          <div className="flex flex-col items-center py-14 text-gray-500 gap-3">
            <Package size={40} className="opacity-30" />
            <p className="text-sm">No add-ons yet. Create one to get started.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-white/5">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Arabic</th>
                <th className="px-5 py-3 font-medium text-right">Price</th>
                <th className="px-5 py-3 font-medium text-center">Products</th>
                <th className="px-5 py-3 font-medium text-center">Active</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {data.addons.map(addon => (
                <tr key={addon.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-5 py-3 font-medium text-white">{addon.name_en}</td>
                  <td className="px-5 py-3 text-gray-400" dir="rtl">{addon.name_ar}</td>
                  <td className="px-5 py-3 text-right text-gray-300">
                    {addon.price === 0 ? (
                      <span className="text-green-400 font-semibold">Free</span>
                    ) : formatCurrency(addon.price)}
                  </td>
                  <td className="px-5 py-3 text-center">
                    <button onClick={() => setAssignPanel(addon)}
                      className="text-xs font-semibold text-gray-400 hover:text-white transition-colors">
                      {addon.product_count ?? 0}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-center">
                    {can(['super_admin', 'operations']) ? (
                      <button onClick={() => toggleActive.mutate({ id: addon.id, active: !addon.active })}
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${addon.active ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-gray-500'}`}>
                        {addon.active ? 'Active' : 'Inactive'}
                      </button>
                    ) : (
                      <span className={`text-xs font-semibold ${addon.active ? 'text-green-400' : 'text-gray-500'}`}>
                        {addon.active ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setAssignPanel(addon)}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors">
                        Products <ChevronRight size={13} />
                      </button>
                      {can(['super_admin', 'operations']) && (
                        <button onClick={() => setModal(addon)}
                          className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                          <Pencil size={14} />
                        </button>
                      )}
                      {can(['super_admin']) && (
                        <button onClick={() => { if (confirm('Delete this add-on?')) deleteAddon.mutate(addon.id) }}
                          className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-red-400 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && <AddonModal addon={modal} onClose={() => setModal(null)} />}
      {assignPanel && <ProductAssignPanel addon={assignPanel} onClose={() => setAssignPanel(null)} />}
    </div>
  )
}
